// Unlock orders placed from inside STORE OS on the store's own accounts at unlock sites that run
// Dhru Fusion (GSM Unlockers, HangUnlock): the sites in Settings, what each order status means,
// and how prices show. Shared by the server and the screens.

export const UNLOCK_API_SLOTS = ['1', '2'] as const;
export type UnlockSlot = (typeof UNLOCK_API_SLOTS)[number];

export function isUnlockSlot(v: unknown): v is UnlockSlot {
  return typeof v === 'string' && (UNLOCK_API_SLOTS as readonly string[]).includes(v);
}

/** The two sites the store uses. The owner can rename them or change the address in Settings. */
export const UNLOCK_API_DEFAULTS: Record<UnlockSlot, { name: string; url: string }> = {
  '1': { name: 'GSM Unlockers', url: 'https://www.gsmunlockers.com' },
  '2': { name: 'HangUnlock', url: 'https://www.hangunlock.com' },
};

export type UnlockField = 'name' | 'url' | 'user' | 'key';

/** The Settings key for one site's name, address, username or API key ("unlock_api_1_key"). */
export function unlockSettingKey(slot: UnlockSlot, field: UnlockField): string {
  return `unlock_api_${slot}_${field}`;
}

export const UNLOCK_SETTING_KEY = /^unlock_api_([12])_(name|url|user|key)$/;

/**
 * sending   STORE OS is sending it right now
 * waiting   the site has the order (new)
 * working   the site is working on it
 * done      the code or result is ready
 * rejected  the site couldn't do it and gave the credit back
 * failed    the site said no before ordering: nothing was charged
 * check     STORE OS couldn't tell if the order went through: look at the site before ordering again
 */
export type UnlockStatus = 'sending' | 'waiting' | 'working' | 'done' | 'rejected' | 'failed' | 'check';

export const UNLOCK_STATUS: Record<UnlockStatus, { label: string; tone: 'blue' | 'green' | 'amber' | 'red' }> = {
  sending: { label: 'Sending', tone: 'blue' },
  waiting: { label: 'Ordered', tone: 'blue' },
  working: { label: 'Working on it', tone: 'blue' },
  done: { label: 'Done', tone: 'green' },
  rejected: { label: 'Rejected, refunded', tone: 'red' },
  failed: { label: 'Not ordered', tone: 'amber' },
  check: { label: 'Check the site', tone: 'amber' },
};

/** Orders the site is still working on: STORE OS asks the site about these on its own. */
export const OPEN_UNLOCK_STATUSES: readonly UnlockStatus[] = ['waiting', 'working'];

/** Dhru's order STATUS: 0 new, 1 in process, 3 rejected (refunded), 4 done. Words some sites send work too. */
export function unlockStatusFromSite(raw: unknown): UnlockStatus {
  const s = String(raw ?? '').trim().toLowerCase();
  if (s === '4') return 'done';
  if (s === '3') return 'rejected';
  if (s === '0') return 'waiting';
  if (s === '1') return 'working';
  // "Unavailable", "Not available", "Unsuccessful" and "Incomplete" are no, so those are read first.
  if (/reject|cancel|refund|not found|fail|unavail|not avail|unsuccess|incomplete|denied/.test(s)) return 'rejected';
  if (/\b(success|successful|completed?|done|finished|delivered)\b|^(code )?available$/.test(s)) return 'done';
  if (/pending|new|wait|queue/.test(s)) return 'waiting';
  // In process, or a code this site uses that isn't known: keep asking.
  return 'working';
}

/**
 * The site's address as it's saved: https and a real host name. Only the address is kept, so a
 * link copied from any page of the site works ("https://www.gsmunlockers.com/orders" becomes
 * "https://www.gsmunlockers.com"). Empty stays empty. Null when it isn't a usable address.
 */
export function unlockSiteOrigin(raw: string): string | null {
  const v = raw.trim();
  if (!v) return '';
  let u: URL;
  try {
    u = new URL(/^[a-z][a-z0-9+.-]*:/i.test(v) ? v : `https://${v}`);
  } catch {
    return null;
  }
  if (u.protocol !== 'https:' || u.username || u.password) return null;
  const host = u.hostname.toLowerCase();
  // A real site name (letters and a dot), not a computer on the store's network or a bare IP.
  if (!/^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z][a-z0-9-]{1,62}$/.test(host)) return null;
  if (/(^|\.)(localhost|local|internal|lan|home|arpa)$/.test(host)) return null;
  return `https://${host}${u.port ? `:${u.port}` : ''}`;
}

/** "$12.50" for dollars, "12.50 EUR" for other money, "" when the price isn't known. */
export function unlockPriceLabel(price: string | null | undefined, currency?: string | null): string {
  if (price === null || price === undefined || price === '') return '';
  const n = Number(price);
  if (!Number.isFinite(n)) return '';
  const cur = (currency ?? '').trim().toUpperCase();
  if (!cur || cur === 'USD' || cur === '$') return `$${n.toFixed(2)}`;
  return `${n.toFixed(2)} ${cur}`;
}
